import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clipboard, Trash2, UserPlus, X } from "lucide-react";

import { ApiError, api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useConfirm } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";

/**
 * Admin page: invite people, approve incoming sign-up requests, and
 * manage the existing roster. There is intentionally no role column —
 * every backstage operator is an admin, so this page reads like a flat
 * list of teammates rather than a permissions matrix.
 */

interface Member {
  id: number;
  email: string;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
}

type RequestStatus = "pending" | "approved" | "rejected";

interface MembershipRequest {
  id: number;
  email: string;
  note: string | null;
  status: RequestStatus;
  requested_at: string;
  decided_at: string | null;
  decided_by_email: string | null;
  decision_note: string | null;
}

export function Teams() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const membersQ = useQuery<Member[]>({
    queryKey: ["team", "members"],
    queryFn: () => api.get<Member[]>("/team/members"),
  });

  const pendingQ = useQuery<MembershipRequest[]>({
    queryKey: ["team", "requests", "pending"],
    queryFn: () =>
      api.get<MembershipRequest[]>("/team/requests?status_filter=pending"),
    refetchInterval: 30_000,
  });

  const historyQ = useQuery<MembershipRequest[]>({
    queryKey: ["team", "requests", "all"],
    queryFn: () => api.get<MembershipRequest[]>("/team/requests?status_filter=all"),
    enabled: historyOpen,
  });

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ["team", "members"] });
    qc.invalidateQueries({ queryKey: ["team", "requests", "pending"] });
    qc.invalidateQueries({ queryKey: ["team", "requests", "all"] });
  }

  const approve = useMutation({
    mutationFn: (id: number) =>
      api.post<MembershipRequest>(`/team/requests/${id}/approve`),
    onSuccess: () => invalidateAll(),
    onError: (err) => setActionError(toMessage(err)),
  });

  const reject = useMutation({
    mutationFn: (id: number) =>
      api.post<MembershipRequest>(`/team/requests/${id}/reject`),
    onSuccess: () => invalidateAll(),
    onError: (err) => setActionError(toMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/team/members/${id}`),
    onSuccess: () => invalidateAll(),
    onError: (err) => setActionError(toMessage(err)),
  });

  const signupUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/signup`
      : "/signup";

  async function copySignupUrl() {
    try {
      await navigator.clipboard.writeText(signupUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can be blocked (e.g. focus / permissions). Fall
      // back to a select-all on a transient input so the operator can
      // copy manually with Cmd-C.
      window.prompt("Copy the sign-up URL:", signupUrl);
    }
  }

  const members = membersQ.data ?? [];
  const pending = pendingQ.data ?? [];

  return (
    <div className="page">
      <PageHeader
        eyebrow="Admin"
        title="Teams"
        subtitle="Invite teammates, approve incoming sign-up requests, and manage the roster. Every member is an admin — there are no separate roles."
      />

      {actionError && (
        <div className="auth-error" style={{ marginBottom: 16 }}>
          {actionError}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginLeft: 8 }}
            onClick={() => setActionError(null)}
          >
            dismiss
          </button>
        </div>
      )}

      <Section
        title="Share sign-up link"
        icon={<UserPlus />}
        count={undefined}
        flush
      >
        <div style={{ padding: "12px 18px 14px", display: "grid", gap: 8 }}>
          <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
            Anyone with this URL can apply for an account. Their application
            shows up below as <em>pending</em>; you decide whether to approve.
          </p>
          <div className="toolbar" style={{ gap: 8, alignItems: "stretch" }}>
            <input
              type="text"
              readOnly
              value={signupUrl}
              onFocus={(ev) => ev.currentTarget.select()}
              className="auth-field__inline-input"
            />
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={copySignupUrl}
            >
              {copied ? <Check /> : <Clipboard />}
              {copied ? "copied" : "copy"}
            </button>
          </div>
        </div>
      </Section>

      <Section
        title="Pending requests"
        icon={<UserPlus />}
        count={`${pending.length}`}
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Email</th>
                <th>Note</th>
                <th style={{ width: 170 }}>Requested</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {pendingQ.isLoading && (
                <tr>
                  <td colSpan={4} className="empty">
                    loading…
                  </td>
                </tr>
              )}
              {!pendingQ.isLoading && pending.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    No pending sign-up requests.
                  </td>
                </tr>
              )}
              {pending.map((req) => (
                <tr key={req.id}>
                  <td className="mono">{req.email}</td>
                  <td className="muted">{req.note || <em>—</em>}</td>
                  <td className="muted">
                    {new Date(req.requested_at).toLocaleString()}
                  </td>
                  <td className="actions">
                    <div className="toolbar" style={{ gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={approve.isPending || reject.isPending}
                        onClick={() => {
                          setActionError(null);
                          approve.mutate(req.id);
                        }}
                      >
                        <Check />
                        approve
                      </button>
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        disabled={approve.isPending || reject.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Reject sign-up request?",
                            tone: "danger",
                            confirmLabel: "Reject",
                            description: (
                              <p style={{ marginTop: 0 }}>
                                Reject the sign-up request from{" "}
                                <strong>{req.email}</strong>?
                              </p>
                            ),
                          });
                          if (!ok) return;
                          setActionError(null);
                          reject.mutate(req.id);
                        }}
                      >
                        <X />
                        reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Members"
        icon={<UserPlus />}
        count={`${members.length}`}
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Email</th>
                <th style={{ width: 170 }}>Joined</th>
                <th style={{ width: 170 }}>Last login</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {membersQ.isLoading && (
                <tr>
                  <td colSpan={4} className="empty">
                    loading…
                  </td>
                </tr>
              )}
              {!membersQ.isLoading && members.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    No members.
                  </td>
                </tr>
              )}
              {members.map((m) => {
                const isSelf = user?.id === m.id;
                return (
                  <tr key={m.id}>
                    <td className="mono">
                      <span style={{ marginRight: 6 }}>{m.email}</span>
                      {isSelf && (
                        <span style={{ marginRight: 6 }}>
                          <Pill tone="brand">you</Pill>
                        </span>
                      )}
                      {m.must_change_password && (
                        <Pill tone="warn">must change password</Pill>
                      )}
                    </td>
                    <td className="muted">
                      {new Date(m.created_at).toLocaleString()}
                    </td>
                    <td className="muted">
                      {m.last_login_at
                        ? new Date(m.last_login_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        disabled={isSelf || remove.isPending}
                        title={
                          isSelf
                            ? "You cannot remove your own account here"
                            : `Remove ${m.email}`
                        }
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Remove member?",
                            tone: "danger",
                            confirmLabel: "Remove",
                            description: (
                              <p style={{ marginTop: 0 }}>
                                Remove <strong>{m.email}</strong>? They will
                                lose access immediately.
                              </p>
                            ),
                          });
                          if (!ok) return;
                          setActionError(null);
                          remove.mutate(m.id);
                        }}
                      >
                        <Trash2 />
                        remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Decision history"
        icon={<UserPlus />}
        count={historyOpen ? `${historyQ.data?.length ?? 0}` : undefined}
        flush
      >
        <div style={{ padding: "12px 18px" }}>
          {!historyOpen ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setHistoryOpen(true)}
            >
              show decided requests
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setHistoryOpen(false)}
            >
              hide
            </button>
          )}
        </div>
        {historyOpen && (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Email</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 170 }}>Requested</th>
                  <th style={{ width: 170 }}>Decided</th>
                  <th>Decided by</th>
                </tr>
              </thead>
              <tbody>
                {(historyQ.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.email}</td>
                    <td>
                      <Pill tone={statusTone(r.status)}>{r.status}</Pill>
                    </td>
                    <td className="muted">
                      {new Date(r.requested_at).toLocaleString()}
                    </td>
                    <td className="muted">
                      {r.decided_at
                        ? new Date(r.decided_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="muted mono">{r.decided_by_email ?? "—"}</td>
                  </tr>
                ))}
                {historyQ.data && historyQ.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      No history yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function statusTone(s: RequestStatus): "ok" | "danger" | "warn" {
  if (s === "approved") return "ok";
  if (s === "rejected") return "danger";
  return "warn";
}

function toMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail =
      (err.body as { detail?: string } | null)?.detail ?? `HTTP ${err.status}`;
    return detail;
  }
  if (err instanceof Error) return err.message;
  return "Request failed.";
}
